import { StateObj, StateDateObj, FiltersData, FilterCalendar, FilterState, CalendarEvent } from './Calendar-Interfaces'

/**
 * Extracts filter parameters from the application state and returns an object.
 * 
 * @param state - The application state containing filter information.
 * @returns An object representing filter parameters.
 */
export function getParamsObjFromState(state: FilterState): StateObj {
    const stateObj: StateObj = { calendars: [], tags: [], date: state.date };
    // Extract calendar and tag information from the state
    state.filters.forEach(calendar => {
        if (calendar.isChecked) {
            stateObj.calendars.push(calendar.calendarID);
        }

        calendar.tags.forEach(tag => {
            if (tag.count > 0 && tag.isChecked && !stateObj.tags.includes(tag.tagID)) {
                stateObj.tags.push(tag.tagID);
            }
        });
    });

    return stateObj;
}


/**
 * Extracts filter parameters from the URL query string or uses default values.
 * Converts a comma-separated string to an array of numbers or strings.
 * 
 * @param byDefault - Default filter parameters.
 * @returns An object representing filter parameters.
 */
export function getUrlParams(byDefault: FiltersData): StateObj {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    // Inline function for converting a comma-separated string to an array of numbers or strings
    const convertToNumberArray = (param: string | null): (number | string)[] | null =>
        param ? param.split(',').map(i => isNaN(Number(i)) ? i.toLowerCase() : Number(i)) : null;

    const categories = convertToNumberArray(urlParams.get('calendars')) || byDefault.calendars;
    const tags = convertToNumberArray(urlParams.get('tags')) || byDefault.tags;
    const dateParam = urlParams.get('date');

    return {
        tags,
        calendars: categories,
        date: dateParam ? parseDateString(dateParam) : null,
    };
}

/**
 * Generates a StateDateObj based on the provided Date object, including the full date, year, month, and day.
 * Also creates a formatted string (urlDate) representing the date in "YYYYMMDD" format.
 *
 * @param selectedDate - The Date object for which to generate the StateDateObj.
 * @returns The StateDateObj representing the selected date.
 */
export function generateDateObj(selectedDate: Date): StateDateObj {
    // Extract month and day components and format them with leading zeros if needed
    const month = ("0" + (selectedDate.getMonth() + 1)).slice(-2);
    const day = ("0" + selectedDate.getDate()).slice(-2);

    return {
        full: selectedDate,
        year: `${selectedDate.getFullYear()}`,
        month: month,
        day: day,
        urlDate: `${selectedDate.getFullYear()}${month}${day}`
    };
};


/**
 * Parses a date string into a StateDateObj, including the full date, year, month, and day.
 * Also creates a formatted string (urlDate) representing the date in "YYYYMMDD" format.
 *
 * @param dateString - The date string to parse, which can have formats like 'YYYYMMDD', 'YYYYMM', 'YYYY', etc.
 * @returns The StateDateObj representing the parsed date, or null if the format is invalid.
 */
function parseDateString(dateString: string): StateDateObj | null {
    let match;

    // Determine the format based on the length of the input string
    if (dateString.length === 7) {
        match = dateString.match(/^(\d{4})(\d{2})(\d{1})|(\d{4})(\d{2})|(\d{4})$/);
    } else if (dateString.length === 5) {
        match = dateString.match(/^(\d{4})(\d{1})|(\d{4})$/);
    } else {
        match = dateString.match(/^(\d{4})(\d{2})(\d{2})|(\d{4})(\d{2})|(\d{4})$/);
    }

    // Validate the format
    if (!match) {
        console.error('Invalid date string format');
        return null;
    }

    const [, year, month, day] = match.filter(Number);

    // Validate the components
    if (isNaN(year) && isNaN(month) && isNaN(day)) {
        console.error('Invalid date components');
        return null;
    }

    const stateDateObj: StateDateObj = {};

    // Set the components in the StateDateObj
    if (!isNaN(year)) {
        stateDateObj.year = year.toString();
    }

    if (!isNaN(month)) {
        stateDateObj.month = month.toString().padStart(2, '0');
    }

    if (!isNaN(day)) {
        stateDateObj.day = day.toString().padStart(2, '0');
    }

    // Create the formatted date string (urlDate) and set the full date
    let dateStr;
    if (stateDateObj.year) {
        stateDateObj.urlDate = `${stateDateObj.year}`;
        dateStr = [stateDateObj.year];

        if (stateDateObj.month) {
            stateDateObj.urlDate += `${stateDateObj.month}`;
            dateStr.push(stateDateObj.month);

            if (stateDateObj.day) {
                stateDateObj.urlDate += `${stateDateObj.day}`;
                dateStr.push(stateDateObj.day);
            }
        }
    }

    if (dateStr) {
        stateDateObj.full = new Date(dateStr); // Assuming the date format is YYYY-MM-DD
    }

    return stateDateObj;
}

/* Get Default Filters */

export function getDefaultFilters(all: boolean = false): FiltersData {
    const getCheckedFilters = (checkboxList: Array<HTMLInputElement>): number[] => {
        return (all ? checkboxList : checkboxList.filter(checkbox => checkbox.checked))
            .map(checkbox => Number(checkbox.value));
    };

    const getDefaultCategories = (): number[] => {
        const categoriesCheckBoxes = Array.from(document.getElementsByName('categories') as NodeListOf<HTMLInputElement>);
        return getCheckedFilters(categoriesCheckBoxes);
    };

    const getDefaultTags = (): number[] => {
        const tagsCheckBoxes = document.getElementById('defaultTagsId') as HTMLInputElement;
        return tagsCheckBoxes && tagsCheckBoxes.value ?  tagsCheckBoxes.value.split(',').map(i => parseInt(i)) : [];
    };

    return { calendars: getDefaultCategories(), tags: getDefaultTags() };
}

export function initFilterState(params: StateObj): FilterState {
    const calendars: Array<FilterCalendar> = []
    const categoriesCheckBoxes = Array.from(document.getElementsByName('categories') as NodeListOf<HTMLInputElement>);
    categoriesCheckBoxes.forEach(calendar => {
        const label = document.querySelector(`label[for="taglist_${calendar.value}"]`).textContent;
        calendars.push({
            calendarID: Number(calendar.value),
            isChecked: params.calendars.some(i => i == Number(calendar.value)),
            calendarName: label,
            tags: []
        })
    })
    return { filters: calendars, date: params.date }
}

export function addTagsToState(state: FilterState, event, tags: (number | string)[], avaliableTags): FilterState {
    const calendar: FilterCalendar = state.filters.find(i => i.calendarID === event.extendedProps.calendarID);
    if (avaliableTags.length > 0 && avaliableTags.includes(event.extendedProps.tagID)) {
        event.extendedProps.tags.forEach(tag => {
            const calendarTag = calendar.tags.find(t => t.tagID === tag.id);

            if (calendarTag) {
                calendarTag.count += 1;
            } else {
                calendar.tags.push({
                    tagID: tag.id,
                    tagName: tag.name,
                    count: 1,
                    isChecked: calendar.isChecked ? tags.some(i => i == tag.id) || false : false,
                });
            }
        });
    }
    return state;
}


/* Get Events By date and categories */
export function getEvents(start: Date | null, end: Date | null, categories: (number | string)[]): Promise<{ calendarEvents: CalendarEvent[] }> {

    const getISOSDate = (date: Date): string => new Date(date).toISOString().replace('Z', '');
    const getPreviousMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() - 6, date.getDate());
    const getNextMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 6, date.getDate());

    const host = window.location;
    const url = new URL(`${host.origin}/api/calendar/getevents`);

    // Calculate start and end dates for URL params
    const urlParams = {
        categories: categories.join(','),
        tags: '',
        start: start ? getISOSDate(getPreviousMonth(start)) : '',
        end: end ? getISOSDate(getNextMonth(end)) : '',
    };

    url.search = new URLSearchParams(urlParams).toString();

    return fetch(url.toString())
        .then(response => response.json())
        .then(data => { return { calendarEvents: data } });
}

/**
 * Updates the displayed tags in the tag filter based on the checked calendars.
 * @param {Array<FilterCalendar>} calendarList - The list of calendars to filter tags.
 */
export function showTags(calendarList: Array<FilterCalendar>): void {
    const checkedCalendars = calendarList.filter(c => c.isChecked);
    const tagFilter = document.getElementById('Calendar_tags');
    const tagFilterList = tagFilter.querySelector('.calendar-filters-list');
    //Clear existing tag filter list
    tagFilterList.innerHTML = '';

    if (checkedCalendars.length > 0) {
        checkedCalendars.forEach(calendar => {
            if (calendar.tags.length > 0 && calendar.tags.some(tag => tag.count > 0)) {
                let itemElement = `<li class="calendar-filters-list-item"><h4>${calendar.calendarName}</h4> <ul class="calendar-filters-list no-bullets">`;
                
                calendar.tags.forEach(tag => {
                    if (tag.count > 0) {
                        itemElement += `<li class="calendar-filters-list-item">
                                        <input type="checkbox" data-tagid="${tag.tagID}" value="${tag.tagID}" id="taglist_${tag.tagID}" name="tags" tabindex="0" ${tag.isChecked && 'checked'}>
                                        <label for="taglist_${tag.tagID}" class="directory-filter-checkbox">${tag.tagName}</label>
                                    </li>`;
                    }
                });

                itemElement += `</ul></li>`;
                tagFilterList.innerHTML += itemElement;
            }
        });
    }
    // Adjust the height of the tag filter
    tagFilter.style.height = `${tagFilterList['offsetHeight'] + 20}px`;
    // Accessibility fix: if aria-hidden is true, remove focusability
    if (tagFilter.getAttribute('aria-hidden') === 'true') {
        const inputs = tagFilter.querySelectorAll('input');
        inputs.forEach(input => {
            input.setAttribute('tabindex', '-1');
        });
    }
}


function updateFilterData(filters: (number | string)[], filterId: number, condition: boolean) {
    condition
        ? filters.push(filterId)
        : filters.splice(filters.findIndex(i => i == filterId), 1)
    return filters
}

export function updateCalendarView(filters: Array<FilterCalendar>): void {
    const categoriesCheckBoxes = Array.from(document.getElementsByName('categories') as NodeListOf<HTMLInputElement>);
    categoriesCheckBoxes.forEach(calendar => {
        calendar.checked = filters.find(i => i.calendarID === Number(calendar.value)).isChecked;
    })
}

/**
 * Updates the state of filter calendars based on the clicked filter IDs.
 * @param filters - Array of FilterCalendar objects representing the filter calendars.
 * @param clickedFilterIds - Array of numbers representing the IDs of the clicked filter calendars.
 */
export function updateFilterCalendarState(filters: Array<FilterCalendar>, clickedFilterIds: number[]) {
    // Iterate through each filter calendar
    filters.forEach(calendar => {
        // Check if the calendar is among the clicked filter IDs
        if (clickedFilterIds.includes(calendar.calendarID)) {
            // If yes, mark the calendar as checked
            calendar.isChecked = true;
        } else {
            // If not, mark the calendar as unchecked and uncheck all associated tags
            calendar.isChecked = false;
            calendar.tags.forEach(tag => { tag.isChecked = false; });
        }
    });
}

export function updateFilterParams(state: StateObj, calendarTags: FilterCalendar): StateObj {
    if (calendarTags && !calendarTags.isChecked && state.tags.length !== 0) {
        const tagIds = calendarTags.tags.map(tag => tag.tagID);
        tagIds.forEach(id => {
            state.tags.some(i => i == id) &&
                state.tags.splice(state.tags.findIndex(i => i == id), 1)
        })
    }
    state.calendars = updateFilterData(state.calendars, calendarTags.calendarID, calendarTags.isChecked);
    return state;
}

export function updateCalendarTags(calendarTag: FilterCalendar, isChecked: boolean) { // ????
    !isChecked && calendarTag.tags.forEach(tag => { tag.isChecked = false; })
    calendarTag.isChecked = isChecked;
    return calendarTag;
}

/**
 * Updates the URL parameters based on the specified state object, reflecting changes in calendars, tags, and date.
 * @param {StateObj} params - An object containing parameters such as calendars, tags, and date to be updated in the URL.
 */
export function updateUrlParams(params: StateObj): void {
    const searchParams = new URLSearchParams(window.location.search);

    // Helper function to update or delete a parameter in the URL.
    const updateOrDeleteParam = (paramKey: string, paramValue: string | null) => {
        if (paramValue !== null && paramValue.length > 0) {
            searchParams.set(paramKey, paramValue);
        } else if (searchParams.has(paramKey)) {
            searchParams.delete(paramKey);
        }
    };

    // Update or delete 'calendars' parameter.
    updateOrDeleteParam('calendars', params.calendars.join(','));

    // Update or delete 'tags' parameter.
    updateOrDeleteParam('tags', params.tags.join(','));

    // Update or delete 'date' parameter.
    updateOrDeleteParam('date', params.date ? params.date.urlDate : null);

    // Build the new URL and update the history.
    const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
    history.pushState(null, '', newRelativePathQuery);
}

/**
 * Checks whether a calendar event matches the specified filters.
 * @param {CalendarEvent} event - The calendar event to be evaluated.
 * @param {StateObj} filters - An object containing filter criteria, such as calendars and tags.
 * @returns {boolean} - True if the event matches the filter criteria, false otherwise.
 */
export function matchesFilters(event: CalendarEvent, filters: StateObj): boolean {
    // Initialize a boolean variable to track whether the event matches the filter criteria.
    let isMatch: boolean = false;
    // Check if there are calendar filters specified.
    if (filters.calendars && filters.calendars.length) {
        // Determine if the event matches any of the specified calendar IDs or names.
        isMatch = filters.calendars.some(filter =>
            typeof filter === 'number'
                ? filter === event.extendedProps.calendarID
                : filter === event.extendedProps.calendarName
        );
    }

    // Check if there are tag filters specified.
    if (filters.tags && filters.tags.length) {
        // Determine if the event matches any of the specified tag IDs or names.
        isMatch = filters.tags.some(filter =>
            typeof filter === 'number'
                ? event.extendedProps.tags.some(tag => tag.id === filter)
                : event.extendedProps.tags.some(tag => tag.name === filter)
        );
    }

    // Return the final result indicating whether the event matches the filter criteria.
    return isMatch;
}

/**
 * Checks if a calendar event intersects with the selected period based on the provided start and end dates.
 * @param event - The calendar event to be checked.
 * @param dateStart - The start date time of the selected period.
 * @param dateEnd - The end date time of the selected period.
 * @returns True if the event intersects with the selected month, otherwise false.
 */
export function doesEventIntersectWithMonth(event: CalendarEvent, dateStart: number, dateEnd: number): boolean {
    // Convert event start and end times to milliseconds
    const eventStart = new Date(event.start).getTime(); // Event start time
    const eventEnd = new Date(event.end).getTime(); // Event end time


    // Check if the event intersects with the selected period
    if (
        // The entire event is within the selected period
        (eventStart >= dateStart && eventEnd <= dateEnd) ||
        // The event starts before the period and ends during or after the selected period
        (eventStart < dateStart && eventEnd > dateStart) ||
        // The event starts within the selected period and ends after the selected period
        (eventStart >= dateStart && eventStart <= dateEnd)
    ) {
        // The event intersects with the selected month
        return true;
    }

    // The event does not intersect with the selected month
    return false;
}